<script setup>
import { computed, ref } from 'vue'
import { useElementSize } from '@vueuse/core'
import AppBadge from '@/components/atoms/AppBadge'
import AppButton from '@/components/atoms/AppButton'
import AppCharacterCount from '@/components/atoms/AppCharacterCount'
import AppCheckbox from '@/components/atoms/AppCheckbox'
import AppProgressBar from '@/components/atoms/AppProgressBar'
import AppMarketplaceBadge from '@/components/molecules/AppMarketplaceBadge'
import { ORDER_STATUS_MAP } from '@/constants'
import rawCardImage from '@/assets/images/place-cards/8.png'
import slabCardImage from '@/assets/images/place-cards/6.png'
import hendersonCardImage from '@/assets/images/home/sample-card-henderson.jpg'

// A Getting Started step's picture: a small scene of the step's own screen (Figma "Illustration", 28784:626725), or
// `image` for a step without one. The caller sizes the box; the scene covers it.
const props = defineProps({
  stepKey: { type: String, default: '' },
  image: { type: String, default: '' },
})

// The scenes are drawn at full size from the app's own atoms on a 446×211 stage (the design's 296×140 picture at
// 2/3), then scaled to cover the box: centred across, top-aligned, since every scene runs off its bottom edge.
const STAGE_WIDTH = 446
const STAGE_HEIGHT = 211
const SCENES = ['connect-marketplaces', 'card-templates', 'add-cards', 'review-cards', 'list-cards', 'view-orders', 'pick-orders']

// The scenes' sample content.
const SAMPLE_TITLE = 'Ken Griffey Jr. 1989 Upper Deck #1 RC'
const CONNECT_ROWS = [
  { marketplace: 'ebay', connected: true },
  { marketplace: 'shopify', next: true },
  { marketplace: 'whatnot' },
]
const TEMPLATE_FIELDS = ['name', 'set', '#card', 'rookie']
// The batch strip: two reviewed cards, the one under review, an empty slot. `framed` pictures carry the scan
// screen's blue frame, which scale-110 crops off.
const REVIEW_THUMBS = [
  { image: hendersonCardImage, reviewed: true },
  { image: slabCardImage, framed: true, reviewed: true },
  { image: rawCardImage, framed: true, current: true },
  { image: null },
]
const REVIEW_FIELDS = [
  { label: 'Player', value: 'Ken Griffey Jr.' },
  { label: 'Set', value: '1989 Upper Deck' },
  { label: 'Card #', value: '1' },
]
const LIST_ROWS = [
  { marketplace: 'ebay', checked: true },
  { marketplace: 'shopify', checked: true },
  { marketplace: 'whatnot', checked: false },
]
const ORDER_ROWS = [
  { marketplace: 'ebay', title: SAMPLE_TITLE, price: '$149.99', status: ORDER_STATUS_MAP.ready_to_ship },
  { marketplace: 'whatnot', title: '2022 Topps Chrome Auto PSA 10', price: '$85.00', status: ORDER_STATUS_MAP.shipped },
  { marketplace: 'shopify', title: '1990 Topps Baseball, 12 cards', price: '$32.50', status: ORDER_STATUS_MAP.completed },
]
const PICK_ROWS = [
  { location: 'Box 2 · Row 4', title: 'Griffey Jr. #1 RC', picked: true },
  { location: 'Box 5 · Row 1', title: 'Topps Chrome Auto', picked: true },
  { location: 'Box 7 · Row 3', title: '1990 Topps lot', picked: false },
]

const box = ref(null)
const { width, height } = useElementSize(box)

const hasScene = computed(() => SCENES.includes(props.stepKey))

const stageStyle = computed(() => {
  if (!width.value || !height.value) return { transform: 'scale(0.7)' }

  const scale = Math.max(width.value / STAGE_WIDTH, height.value / STAGE_HEIGHT)

  return { left: `${(width.value - STAGE_WIDTH * scale) / 2}px`, transform: `scale(${scale})` }
})
</script>

<template>
  <div ref="box" class="relative overflow-hidden" :class="{ 'bg-blue-50': hasScene }">
    <div v-if="hasScene" aria-hidden="true" inert class="absolute left-0 top-0 h-[211px] w-[446px] origin-top-left" :style="stageStyle">
      <!-- Connections: eBay connected, Shopify next. -->
      <template v-if="stepKey === 'connect-marketplaces'">
        <div class="absolute left-1/2 top-[63.5px] w-[300px] -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-[18px] pt-3 shadow-md">
          <p class="text-[15px] font-semibold leading-5 text-gray-900">Connections</p>

          <ul class="divide-y divide-gray-100">
            <li v-for="row in CONNECT_ROWS" :key="row.marketplace" class="flex items-center justify-between py-[9px]">
              <AppMarketplaceBadge :marketplace="row.marketplace" label-class="text-sm font-medium text-gray-900" />
              <span v-if="row.connected" class="flex h-6 w-[89px] items-center justify-center rounded-[9px] bg-green-100 text-[13px] font-medium text-green-800">Connected</span>
              <AppButton v-else size="xs" :variant="row.next ? 'default' : 'outlined'">Connect</AppButton>
            </li>
          </ul>
        </div>

        <Icon icon="mdi:cursor-default" class="absolute left-[318px] top-[164px] size-6 text-gray-900 [&_path]:stroke-white [&_path]:stroke-[1.5px]" />
      </template>

      <!-- Templates: a title template being typed, with the card it describes. -->
      <template v-else-if="stepKey === 'card-templates'">
        <div class="absolute left-9 top-[64px] w-[300px] rounded-xl border border-gray-200 bg-white p-3.5 shadow-md">
          <div class="flex items-center gap-x-2">
            <AppMarketplaceBadge marketplace="ebay" size="sm" :show-label="false" />
            <p class="text-sm font-semibold text-gray-900">Title template</p>
          </div>

          <div class="mt-2.5 flex items-center gap-1 rounded-lg border border-tint-1 px-2 py-1.5 ring-2 ring-blue-100">
            <AppBadge v-for="field in TEMPLATE_FIELDS" :key="field" variant="tint-light" size="xs">{{ field }}</AppBadge>
            <span class="h-4 w-px bg-tint-1" />
          </div>

          <p class="mt-2.5 text-xs text-gray-500">Preview</p>
          <p class="mt-0.5 text-sm font-medium text-gray-900" v-text="SAMPLE_TITLE" />
          <AppCharacterCount :length="SAMPLE_TITLE.length" :max="80" />
        </div>

        <span class="absolute left-[318px] top-[78px] h-[100px] w-[72px] rotate-6 overflow-hidden rounded-md shadow-lg">
          <img :src="rawCardImage" alt="" class="size-full scale-110 object-cover" />
        </span>
      </template>

      <!-- Add cards: photos uploading, or an import. -->
      <template v-else-if="stepKey === 'add-cards'">
        <div class="absolute inset-x-8 top-[76px] flex h-40 justify-end rounded-xl border-2 border-dashed border-blue-300 bg-white/80 pr-6 pt-9">
          <div class="flex flex-col items-start gap-y-2">
            <p class="flex items-center gap-x-1.5 text-sm font-medium text-gray-900">
              <Icon icon="heroicons:arrow-up-tray-16-solid" class="size-4 text-tint-1" />
              Uploading 24 photos
            </p>
            <AppProgressBar :value="62" width="w-40" />
            <p class="mt-1 flex items-center gap-x-1.5 text-xs text-gray-500">
              or import from
              <AppMarketplaceBadge v-for="marketplace in ['ebay', 'shopify', 'whatnot']" :key="marketplace" :marketplace="marketplace" size="sm" :show-label="false" />
            </p>
          </div>
        </div>

        <span class="absolute left-[51.5px] top-[55px] h-[104px] w-[75px] -rotate-12 overflow-hidden rounded-md shadow-lg">
          <img :src="slabCardImage" alt="" class="size-full scale-110 object-cover" />
        </span>
        <span class="absolute left-[97px] top-[40.5px] h-[104px] w-[75px] -rotate-2 overflow-hidden rounded-md shadow-lg">
          <img :src="rawCardImage" alt="" class="size-full scale-110 object-cover" />
        </span>
        <span class="absolute left-[141px] top-[59px] flex h-[104px] w-[75px] rotate-12 items-center justify-center rounded-md border border-gray-200 bg-gray-100 shadow-lg">
          <Icon icon="svg-spinners:90-ring-with-bg" class="size-[25px] text-tint-1" />
        </span>
      </template>

      <!-- Review: the third card of a batch, one field still to fill. -->
      <template v-else-if="stepKey === 'review-cards'">
        <div class="absolute left-[18px] top-[12.7px] flex gap-x-1.5">
          <span v-for="(thumb, index) in REVIEW_THUMBS" :key="index" class="relative h-11 w-8 rounded" :class="{ 'ring-2 ring-tint-1 ring-offset-1': thumb.current }">
            <span class="block size-full overflow-hidden rounded" :class="thumb.image ? 'shadow' : 'border border-gray-200 bg-gray-100'">
              <img v-if="thumb.image" :src="thumb.image" alt="" class="size-full object-cover" :class="{ 'scale-110': thumb.framed }" />
            </span>
            <Icon v-if="thumb.reviewed" icon="heroicons:check-circle-16-solid" class="absolute -right-1.5 -top-1.5 size-4 rounded-full bg-white text-green-500" />
          </span>
        </div>

        <div class="absolute left-[27px] top-[69px] w-[392px] rounded-xl border border-gray-200 bg-white p-3 shadow-md">
          <div class="flex items-center justify-between">
            <p class="text-xs text-gray-500">Card 3 of 24</p>
            <AppBadge variant="yellow" size="xs">Needs review</AppBadge>
          </div>

          <div class="mt-2 flex gap-x-4">
            <span class="h-28 w-20 flex-none overflow-hidden rounded-md shadow">
              <img :src="rawCardImage" alt="" class="size-full scale-110 object-cover" />
            </span>

            <dl class="grid flex-1 grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2.5 text-xs">
              <template v-for="field in REVIEW_FIELDS" :key="field.label">
                <dt class="text-gray-500" v-text="field.label" />
                <dd class="flex items-center justify-between font-medium text-gray-900">
                  {{ field.value }}
                  <Icon icon="heroicons:check-circle-16-solid" class="size-4 text-green-500" />
                </dd>
              </template>
              <dt class="text-gray-500">Condition</dt>
              <dd class="flex h-7 items-center rounded-md border border-tint-1 px-2 ring-2 ring-blue-100">
                <span class="h-3.5 w-px bg-tint-1" />
              </dd>
            </dl>
          </div>
        </div>
      </template>

      <!-- List: the marketplaces picked for a dozen cards. -->
      <template v-else-if="stepKey === 'list-cards'">
        <span class="absolute left-[47px] top-[72px] h-[100px] w-[72px] -rotate-12 overflow-hidden rounded-md shadow-lg">
          <img :src="slabCardImage" alt="" class="size-full scale-110 object-cover" />
        </span>
        <span class="absolute left-[89px] top-[60px] h-[100px] w-[72px] -rotate-3 overflow-hidden rounded-md shadow-lg">
          <img :src="rawCardImage" alt="" class="size-full scale-110 object-cover" />
        </span>

        <div class="absolute left-[158px] top-14 w-[250px] rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
          <ul class="flex flex-col gap-y-2.5">
            <li v-for="row in LIST_ROWS" :key="row.marketplace" class="flex items-center gap-x-2.5">
              <AppCheckbox :model-value="row.checked" />
              <AppMarketplaceBadge :marketplace="row.marketplace" size="sm" label-class="text-sm font-medium text-gray-900" />
            </li>
          </ul>

          <AppButton variant="default" size="sm" class="mt-3 w-full">List 12 cards</AppButton>
        </div>

        <Icon icon="mdi:cursor-default" class="absolute left-[199px] top-[178px] size-6 text-gray-900 [&_path]:stroke-white [&_path]:stroke-[1.5px]" />
      </template>

      <!-- Orders: the month's sales, one just in. -->
      <template v-else-if="stepKey === 'view-orders'">
        <div class="absolute left-[18px] top-[18px] flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white py-1.5 pl-1.5 pr-3 shadow-lg">
          <span class="flex size-6 items-center justify-center rounded-full bg-green-100">
            <Icon icon="heroicons:currency-dollar-16-solid" class="size-4 text-green-500" />
          </span>
          <span class="text-xs font-medium text-gray-900">New sale</span>
          <span class="text-xs font-semibold text-green-500">+$149.99</span>
        </div>

        <div class="absolute left-1/2 top-[63px] w-[392px] -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-4 pt-3 shadow-md">
          <div class="flex items-center justify-between">
            <p class="text-sm font-semibold text-gray-900">Orders</p>
            <p class="text-xs text-gray-500">Last 30 days</p>
          </div>

          <ul class="divide-y divide-gray-100">
            <li v-for="order in ORDER_ROWS" :key="order.title" class="flex items-center gap-x-3 py-2.5">
              <AppMarketplaceBadge :marketplace="order.marketplace" :show-label="false" />
              <p class="min-w-0 flex-1 truncate text-xs font-medium text-gray-900" v-text="order.title" />
              <p class="text-xs font-semibold text-gray-900" v-text="order.price" />
              <AppBadge :variant="order.status.variant" size="xs" class="w-24 justify-center">{{ order.status.label }}</AppBadge>
            </li>
          </ul>
        </div>
      </template>

      <!-- Pick list: two picked, one to go, and the route through the shelves. -->
      <template v-else-if="stepKey === 'pick-orders'">
        <!-- The design draws the list at 92%. -->
        <div class="absolute left-[18px] top-[34.65px] h-[175px] w-[196px] origin-top-left scale-[0.923] rounded-xl border border-gray-200 bg-white px-2 pt-3 shadow-md">
          <p class="px-1.5 text-sm font-semibold text-gray-900">Pick List</p>

          <ul class="mt-1.5 flex flex-col">
            <li
              v-for="(pick, index) in PICK_ROWS"
              :key="pick.location"
              class="flex items-center gap-x-2 rounded-md px-1.5 py-1.5"
              :class="{ 'bg-blue-50': !pick.picked }"
            >
              <AppCheckbox :model-value="pick.picked" />
              <div class="min-w-0">
                <p class="text-xs font-medium" :class="pick.picked ? 'text-gray-400 line-through' : 'text-gray-900'">{{ index + 1 }}. {{ pick.location }}</p>
                <p class="truncate text-[11px] text-gray-500" v-text="pick.title" />
              </div>
            </li>
          </ul>
        </div>

        <div class="absolute left-[248px] top-[60px] h-[170px] w-[180px] rounded-xl border border-gray-200 bg-white shadow-md">
          <span class="absolute left-5 top-6 h-24 w-6 rounded bg-gray-100" />
          <span class="absolute left-[78px] top-6 h-24 w-6 rounded bg-gray-100" />
          <span class="absolute left-[136px] top-6 h-24 w-6 rounded bg-gray-100" />
          <svg viewBox="0 0 180 170" class="absolute left-0 top-[7.5px] h-[170px] w-[180px]" fill="none">
            <path d="M32 140 V120 Q32 112 40 112 H58 Q66 112 66 104 V40 Q66 30 76 30 H110 Q120 30 120 40 V112 Q120 120 128 120 H148" class="stroke-tint-1" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round" />
          </svg>
          <span class="absolute left-14 top-16 flex size-5 items-center justify-center rounded-full bg-tint-1 text-white">
            <Icon icon="heroicons:check-16-solid" class="size-3.5" />
          </span>
          <span class="absolute left-[110px] top-16 flex size-5 items-center justify-center rounded-full bg-tint-1 text-white">
            <Icon icon="heroicons:check-16-solid" class="size-3.5" />
          </span>
          <span class="absolute left-[138px] top-[110px] flex size-5 items-center justify-center rounded-full border-2 border-tint-1 bg-white text-[10px] font-semibold text-tint-1">3</span>
        </div>
      </template>
    </div>

    <img v-else :src="image" alt="" loading="lazy" class="block size-full object-cover" />
  </div>
</template>
